import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm, useTranslation } from 'hooks';
import { FormContainer, FormItem } from 'components/styledComponents';
import {
  FormRowContainer,
  Explain,
  ButtonContainer,
  Button,
  Section,
  FlexContainer
} from './style';
import { TextField } from 'components/inputs';
import { Subtitle } from 'components/pages/PlacementsPage/cards/style';
import Dialog from 'components/Dialog';
import MonthSelector from './../MonthSelector';
import { Border } from 'components/Placements/TimeTracking/TimeTrackingPopper/style';

const today = new Date();
const lastBusinessDayBefore = (year, month, day) => {
  let offset = day || 0;
  let result = null;

  if (!day) {
    const date = new Date(year, month, 0);
    date.setMonth(date.getMonth() + 1);
    year = date.getFullYear();
    month = date.getMonth();
  }

  do {
    result = new Date(year, month, offset);
    offset--;
  } while (result.getDay() === 0 || result.getDay() === 6);

  return result;
};

function MonthlyReport({
  className,
  placementDetails,
  year,
  month,
  loading,
  onMonthChanged,
  onSave,
  onSend,
  isMonthlyReportSent,
  setDisableMouseControls
}) {
  const { t } = useTranslation();
  const { currentMonth, currentYear } = placementDetails;

  const reportBillingViaPortal = placementDetails?.placement?.reportBillingViaPortal;

  const [monthlyReport, setMonthlyReport] = useState(
    placementDetails?.months?.[`${year}_${month}`]
      ? { ...placementDetails.months[`${year}_${month}`].monthlyReport }
      : {}
  );

  const [finishMonthOpen, setFinishMonthState] = useState(false);

  useEffect(() => {
    setMonthlyReport(
      placementDetails?.months?.[`${year}_${month}`]
        ? { ...placementDetails.months[`${year}_${month}`].monthlyReport }
        : {}
    );
  }, [placementDetails, year, month]);

  useEffect(() => setFinishMonthState(false), [loading]);

  useEffect(() => {
    if (isMonthlyReportSent) {
      onMonthChanged(new Date(`${currentYear}-${currentMonth}-01`));
    }
  }, [isMonthlyReportSent, currentMonth, currentYear, onMonthChanged]);

  const { useInput, values } = useForm({ initialValues: monthlyReport });

  const handleFocus = () => setDisableMouseControls(true);
  const handleBlur = () => setDisableMouseControls(false);

  const [disabled, finishMonthDisabled] = useMemo(() => {
    let disabled =
      placementDetails.currentMonth !== month ||
      placementDetails.currentYear !== year ||
      new Date(placementDetails.placement.startDate) > today;

    let finishMonthDisabled = disabled;

    if (!disabled) {
      let lastWorkDay = lastBusinessDayBefore(parseInt(year), parseInt(month) - 1);

      if (placementDetails.placement.endDate) {
        const endDate = new Date(placementDetails.placement.endDate);
        const lastPlacementWorkDay = lastBusinessDayBefore(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate()
        );
        if (lastPlacementWorkDay <= lastWorkDay) {
          lastWorkDay = lastPlacementWorkDay;
        }
      }

      finishMonthDisabled = !(lastWorkDay <= today);

      if (
        !finishMonthDisabled &&
        placementDetails.months?.[`${year}_${month}`]?.monthlyReport?.wasSent
      ) {
        finishMonthDisabled = true;
        disabled = true;
      }
    }

    return [disabled, finishMonthDisabled];
  }, [placementDetails, year, month]);

  const isBillingStatusOpen = useMemo(() => monthlyReport.billingStatus === '100', [
    monthlyReport.billingStatus
  ]);

  return (
    <>
      {reportBillingViaPortal !== '100' && (
        <MonthSelector
          month={month}
          placementDetails={placementDetails}
          year={year}
          onMonthChanged={onMonthChanged}
        />
      )}
      <FormContainer className={className}>
        <FormRowContainer>
          <FlexContainer>
            <Subtitle>1. {t('title.monthlyReportSituation')}</Subtitle>
            <span>
              <Button
                disabled={!isBillingStatusOpen}
                color="primary"
                label={t('button.saveReport')}
                onClick={() => onSave(placementDetails.placement.id, values)}
              />
            </span>
          </FlexContainer>
          <Border />
          <Explain>{t('label.monthlyReportSituation')}</Explain>
          <FormItem
            style={{
              marginTop: '16px',
              marginBottom: '8px'
            }}
            component={TextField}
            disabled={!isBillingStatusOpen}
            multiline
            placeholder={t('label.typeSomething')}
            rows={8}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...useInput('situation')}
          />
        </FormRowContainer>

        <FormRowContainer>
          <Subtitle $underlined>2. {t('title.monthlyReportActions')}</Subtitle>
          <FormItem
            style={{
              marginTop: '16px',
              marginBottom: '8px'
            }}
            component={TextField}
            disabled={!isBillingStatusOpen}
            multiline
            placeholder={t('label.typeSomething')}
            rows={8}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...useInput('processedTasksAndActions')}
          />
        </FormRowContainer>

        <FormRowContainer>
          <Subtitle $underlined>3. {t('title.monthlyReportGoals')}</Subtitle>
          <Explain>{t('label.monthlyReportGoals')}</Explain>
          <FormItem
            style={{
              marginTop: '16px',
              marginBottom: '8px'
            }}
            component={TextField}
            disabled={!isBillingStatusOpen}
            multiline
            placeholder={t('label.typeSomething')}
            rows={8}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...useInput('goalAchievementsResults')}
          />
        </FormRowContainer>

        <FormRowContainer>
          <Subtitle>4. {t('title.monthlyReportInhibitions')}</Subtitle>
          <FormItem
            style={{
              marginTop: '16px',
              marginBottom: '8px'
            }}
            component={TextField}
            disabled={!isBillingStatusOpen}
            multiline
            placeholder={t('label.typeSomething')}
            rows={8}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...useInput('criticalInhibitoryFactors')}
          />
        </FormRowContainer>

        <FormRowContainer>
          <Subtitle $underlined>5. {t('title.monthlyReportClientRelation')}</Subtitle>
          <Explain>{t('label.monthlyReportClientRelation')}</Explain>
          <FormItem
            style={{
              marginTop: '16px',
              marginBottom: '8px'
            }}
            component={TextField}
            disabled={!isBillingStatusOpen}
            multiline
            placeholder={t('label.typeSomething')}
            rows={8}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...useInput('relationshipWithClient')}
          />
        </FormRowContainer>

        <FormRowContainer>
          <Subtitle $underlined>6. {t('title.monthlyReportNeededSupport')}</Subtitle>
          <FormItem
            style={{
              marginTop: '16px',
              marginBottom: '8px'
            }}
            component={TextField}
            disabled={!isBillingStatusOpen}
            multiline
            placeholder={t('label.typeSomething')}
            rows={8}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...useInput('supportNeccesary')}
          />
        </FormRowContainer>

        <FormRowContainer>
          <Subtitle $underlined>7. {t('title.monthlyReportOtherRemarks')}</Subtitle>
          <FormItem
            style={{
              marginTop: '16px',
              marginBottom: '8px'
            }}
            component={TextField}
            disabled={!isBillingStatusOpen}
            multiline
            placeholder={t('label.typeSomething')}
            rows={8}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...useInput('otherRemarks')}
          />
        </FormRowContainer>

        {reportBillingViaPortal === '100' && (
          <ButtonContainer>
            <Section $justify="flex-end" $wrap="wrap-reverse">
              <Button
                disabled={!isBillingStatusOpen}
                label={t('button.saveReport')}
                color="primary"
                onClick={() => onSave(placementDetails.placement.id, values)}
                disabledColor="#ededed"
              />
            </Section>
          </ButtonContainer>
        )}
      </FormContainer>

      <Dialog
        actionLabel={t('button.submit')}
        disabled={loading}
        open={!!finishMonthOpen}
        title={t('title.timeTrackingFinishMonthConfirm')}
        onAction={() => onSend(placementDetails.placement.id, values, month, year)}
        onClose={() => setFinishMonthState(false)}
      >
        <div>
          <p>{t('message.timeTrackingFinishMonthConfirm')}</p>
        </div>
      </Dialog>
    </>
  );
}

MonthlyReport.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  placementDetails: PropTypes.object.isRequired,
  year: PropTypes.number,
  month: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onMonthChanged: PropTypes.func.isRequired,
  isMonthlyReportSent: PropTypes.bool,
  setDisableMouseControls: PropTypes.func
};

export default MonthlyReport;
